import { useMemo, useState } from "react";
import { resultCategories, taggedArticles } from "@/mocks/articles";

type SortOrder = "desc" | "asc";
type ArchiveFilter = "all" | "archived" | "unarchived";

const sortOptions: { key: SortOrder; label: string }[] = [
  { key: "desc", label: "预测得分：从高到低" },
  { key: "asc", label: "预测得分：从低到高" },
];

const archiveOptions: { key: ArchiveFilter; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "archived", label: "已归档" },
  { key: "unarchived", label: "未归档" },
];

export default function Results() {
  const [activeKey, setActiveKey] = useState(resultCategories[0].key);
  const [showTip, setShowTip] = useState(false);
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [archiveFilter, setArchiveFilter] = useState<ArchiveFilter>("all");

  const activeCategory =
    resultCategories.find((item) => item.key === activeKey) || resultCategories[0];

  const articles = useMemo(() => {
    const list = taggedArticles.filter((article) => {
      if (article.category !== activeKey) return false;
      if (archiveFilter === "archived") return article.archived;
      if (archiveFilter === "unarchived") return !article.archived;
      return true;
    });
    return [...list].sort((a, b) =>
      sortOrder === "desc" ? b.score - a.score : a.score - b.score
    );
  }, [activeKey, sortOrder, archiveFilter]);

  return (
    <div className="mx-auto w-full max-w-[1200px] px-4 py-8 md:px-8">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground-950">打标生产结果</h1>
          <p className="mt-1 text-sm text-foreground-500">
            按分类查看自动打标结果，包含标签、文章来源、日期、长度与预测得分。
          </p>
        </div>
        <span className="w-fit rounded-full bg-accent-100 px-3 py-1 text-xs text-accent-800">
          共 {taggedArticles.length} 篇已打标
        </span>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-1 rounded-full border border-background-200 bg-background-50 p-1">
        {resultCategories.map((category) => (
          <button
            key={category.key}
            type="button"
            onClick={() => setActiveKey(category.key)}
            className={[
              "cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors",
              category.key === activeKey
                ? "bg-primary-500 text-background-50"
                : "text-foreground-600 hover:bg-background-100",
            ].join(" ")}
          >
            {category.label}
          </button>
        ))}
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-background-200 pb-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-foreground-600">正确</span>
          <span className="text-lg font-semibold text-accent-600">
            {activeCategory.correct}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-foreground-600">错误分入</span>
          <span className="text-lg font-semibold text-foreground-800">
            {activeCategory.wrongIn}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-foreground-600">错误未分入</span>
          <span className="text-lg font-semibold text-foreground-800">
            {activeCategory.wrongOut}
          </span>
        </div>
        <div className="relative ml-1">
          <button
            type="button"
            onMouseEnter={() => setShowTip(true)}
            onMouseLeave={() => setShowTip(false)}
            className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-full text-foreground-400 hover:bg-background-100"
            aria-label="指标说明"
          >
            <i className="ri-information-line" />
          </button>
          {showTip ? (
            <div className="absolute left-0 top-8 z-20 w-64 rounded-md border border-background-200 bg-background-50 p-3 text-xs leading-relaxed text-foreground-600">
              <p><strong className="text-foreground-900">正确</strong>：预测分类与真实分类一致。</p>
              <p className="mt-1"><strong className="text-foreground-900">错误分入</strong>：其它分类被误判为当前分类。</p>
              <p className="mt-1"><strong className="text-foreground-900">错误未分入</strong>：当前分类被误判到其它分类。</p>
            </div>
          ) : null}
        </div>
      </div>

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1.5 text-xs font-medium text-foreground-500">
            <i className="ri-filter-3-line text-sm" />
            归档状态
          </span>
          <div className="flex flex-wrap items-center gap-1 rounded-full border border-background-200 bg-background-50 p-1">
            {archiveOptions.map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => setArchiveFilter(option.key)}
                className={[
                  "cursor-pointer whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors",
                  option.key === archiveFilter
                    ? "bg-secondary-500 text-background-50"
                    : "text-foreground-600 hover:bg-background-100",
                ].join(" ")}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1.5 text-xs font-medium text-foreground-500">
            <i className="ri-sort-desc text-sm" />
            排序
          </span>
          <div className="flex flex-wrap items-center gap-1 rounded-full border border-background-200 bg-background-50 p-1">
            {sortOptions.map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => setSortOrder(option.key)}
                className={[
                  "cursor-pointer whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors",
                  option.key === sortOrder
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-600 hover:bg-background-100",
                ].join(" ")}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-4 text-xs text-foreground-500">
        当前筛选结果 {articles.length} 篇
      </div>

      <div className="mt-3 flex flex-col gap-4">
        {articles.map((article) => (
          <article
            key={article.id}
            className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 md:p-5"
          >
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-md bg-primary-50 text-xs font-bold text-primary-600">
              NEWS
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <h3 className="text-sm font-semibold text-foreground-950 md:text-base">
                  {article.title}
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full bg-secondary-100 px-2 py-0.5 text-xs text-secondary-900"
                    >
                      {tag}
                    </span>
                  ))}
                  <span
                    className={[
                      "flex items-center gap-1 rounded-full px-2 py-0.5 text-xs",
                      article.archived
                        ? "bg-accent-100 text-accent-800"
                        : "bg-background-200/70 text-foreground-600",
                    ].join(" ")}
                  >
                    <i
                      className={
                        article.archived ? "ri-archive-fill text-xs" : "ri-archive-line text-xs"
                      }
                    />
                    {article.archived ? "已归档" : "未归档"}
                  </span>
                </div>
              </div>
              <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-foreground-600 md:text-sm">
                {article.content}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-foreground-500">
                <span>来源：{article.source}</span>
                <span>发表日期：{article.date}</span>
                <span>文章长度：{article.words} 字符</span>
                <span className="text-secondary-900">预测得分：{article.score}</span>
              </div>
            </div>
          </article>
        ))}

        {articles.length === 0 ? (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center text-sm text-foreground-500">
            当前筛选条件下暂无打标结果
          </div>
        ) : null}
      </div>
    </div>
  );
}